import LISTING_DETAILS_SNAPSHOT from '../../../rental-portal-mock-data/listing-details.json'
import {
  HTTP_STATUS_INTERNAL_SERVER_ERROR,
  HTTP_STATUS_NOT_FOUND,
} from '../../constants/api'
import { EMPTY_LIST } from '../../constants/common'
import {
  RENTAL_PORTAL_MOCK_LATENCY,
  RENTAL_PORTAL_MOCK_STATE,
} from '../../constants/rentalPortalMockState'
import { toRentalPortalMockMediaPhoto } from '../../utils/rentalPortalMockMedia'
import { hasRentalPortalMockState } from '../../utils/rentalPortalMockState'

function toMockListingPhoto(photo) {
  return { ...toRentalPortalMockMediaPhoto(photo), mime_type: 'image/jpeg' }
}

function toMockListingCoverPhoto(photo) {
  return photo
    ? { file_id: photo.file_id, thumb_url: photo.thumb_url, url: photo.url }
    : null
}

const MOCK_LISTING_PHOTOS_SNAPSHOT_BY_ID = new Map(
  LISTING_DETAILS_SNAPSHOT.map(detail => [
    detail.id,
    (detail.photos ?? EMPTY_LIST).map(toMockListingPhoto),
  ])
)

const MOCK_LISTING_PHOTOS_BY_ID = new Map()

let nextMockListingPhotoId = 900001

const MOCK_LISTING_PHOTO_WRITE_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

const MOCK_LISTING_PHOTO_NOT_FOUND_ERROR = {
  response: { status: HTTP_STATUS_NOT_FOUND },
}

function withMockPhotoLatency() {
  const delay =
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError) ||
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.loading)
      ? RENTAL_PORTAL_MOCK_LATENCY
      : 0

  return new Promise(resolve => {
    setTimeout(resolve, delay)
  })
}

function getMockListingPhotosSource(listingId) {
  const id = Number(listingId)

  if (!MOCK_LISTING_PHOTOS_BY_ID.has(id)) {
    MOCK_LISTING_PHOTOS_BY_ID.set(id, [
      ...(MOCK_LISTING_PHOTOS_SNAPSHOT_BY_ID.get(id) ?? EMPTY_LIST),
    ])
  }

  return MOCK_LISTING_PHOTOS_BY_ID.get(id)
}

function toMockListingPhotoRows(photos) {
  const coverIndex = Math.max(
    photos.findIndex(photo => photo.is_cover),
    0
  )

  return photos.map((photo, index) => ({
    ...photo,
    is_cover: index === coverIndex,
    position: index + 1,
  }))
}

function setMockListingPhotos(listingId, photos) {
  const rows = toMockListingPhotoRows(photos)

  MOCK_LISTING_PHOTOS_BY_ID.set(Number(listingId), rows)

  return rows
}

function getMockListingPhotosInPayloadOrder(rows, payload) {
  const byFileId = new Map(rows.map(row => [row.file_id, row]))

  const ordered = [...payload]
    .sort((a, b) => a.position - b.position)
    .map(item => {
      const row = byFileId.get(item.file_id)

      byFileId.delete(item.file_id)

      return row ? { ...row, is_cover: Boolean(item.is_cover) } : null
    })
    .filter(Boolean)

  return [...ordered, ...byFileId.values()]
}

export function getMockListingPhotos(listingId) {
  return listingId ? [...getMockListingPhotosSource(listingId)] : EMPTY_LIST
}

export function getMockListingPhotoCount(listingId) {
  return getMockListingPhotos(listingId).length
}

export function getMockListingCoverPhoto(listingId) {
  return listingId
    ? toMockListingCoverPhoto(
        getMockListingPhotosSource(listingId).find(photo => photo.is_cover)
      )
    : null
}

export function getMockListingPhotosPatch(listingId) {
  return {
    cover_photo: getMockListingCoverPhoto(listingId),
    photos: getMockListingPhotos(listingId),
  }
}

export function attachMockListingPhotos(listingId, { files, photos }) {
  return withMockPhotoLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_LISTING_PHOTO_WRITE_ERROR)
    }

    if (!listingId) {
      return Promise.reject(MOCK_LISTING_PHOTO_NOT_FOUND_ERROR)
    }

    const fileById = new Map(files.map(file => [file.id, file]))

    const attached = [...photos]
      .sort((a, b) => a.position - b.position)
      .map(item => {
        const file = fileById.get(item.file_id)

        if (!file) {
          return null
        }

        const id = nextMockListingPhotoId

        nextMockListingPhotoId += 1

        return {
          alt: file.filename || file.name || null,
          file_id: item.file_id,
          filename: file.filename || file.name || null,
          height: file.height ?? null,
          id,
          is_cover: Boolean(item.is_cover),
          mime_type: file.mime_type ?? null,
          position: item.position,
          thumb_url: file.thumbnail || file.origin || null,
          uploaded_at: new Date().toISOString(),
          url: file.origin || null,
          width: file.width ?? null,
        }
      })
      .filter(Boolean)

    return {
      data: setMockListingPhotos(
        listingId,
        [...attached, ...getMockListingPhotosSource(listingId)].sort(
          (a, b) => a.position - b.position
        )
      ),
    }
  })
}

export function setMockListingPhotoOrder(listingId, photos) {
  return withMockPhotoLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_LISTING_PHOTO_WRITE_ERROR)
    }

    if (!listingId) {
      return Promise.reject(MOCK_LISTING_PHOTO_NOT_FOUND_ERROR)
    }

    return {
      data: setMockListingPhotos(
        listingId,
        getMockListingPhotosInPayloadOrder(
          getMockListingPhotosSource(listingId),
          photos
        )
      ),
    }
  })
}

export function removeMockListingPhoto(listingId, fileId) {
  return withMockPhotoLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_LISTING_PHOTO_WRITE_ERROR)
    }

    const source = getMockListingPhotosSource(listingId)

    if (!source.some(photo => photo.file_id === fileId)) {
      return Promise.reject(MOCK_LISTING_PHOTO_NOT_FOUND_ERROR)
    }

    return {
      data: setMockListingPhotos(
        listingId,
        source.filter(photo => photo.file_id !== fileId)
      ),
    }
  })
}
